import { createSelector } from '@reduxjs/toolkit';

import { compareId } from 'mastodon/compare_id';
import type { NotificationGroup } from 'mastodon/models/notification_group';
import type { NotificationGap } from 'mastodon/reducers/notification_groups';
import type { RootState } from 'mastodon/store';

import { isRedesignEnabled } from '../utils/environment';

import {
  selectSettingsNotificationsExcludedTypes,
  selectSettingsNotificationsQuickFilterActive,
  selectSettingsNotificationsQuickFilterShow,
} from './settings';

const filterNotificationsByAllowedTypes = (
  showFilterBar: boolean,
  allowedType: string,
  excludedTypes: string[],
  notifications: (NotificationGroup | NotificationGap)[],
) => {
  if (!showFilterBar || allowedType === 'all') {
    // used if user changed the notification settings after loading the notifications from the server
    // otherwise a list of notifications will come pre-filtered from the backend
    // we need to turn it off for FilterBar in order not to block ourselves from seeing a specific category
    return notifications.filter(
      (item) => item.type === 'gap' || !excludedTypes.includes(item.type),
    );
  }
  return notifications.filter(
    (item) =>
      item.type === 'gap' ||
      allowedType === item.type ||
      (allowedType === 'mention' && item.type === 'quote') ||
      (allowedType === 'collection' &&
        (item.type === 'collection_update' ||
          item.type === 'added_to_collection')),
  );
};

export const selectNotificationGroups = createSelector(
  [
    selectSettingsNotificationsQuickFilterShow,
    selectSettingsNotificationsQuickFilterActive,
    selectSettingsNotificationsExcludedTypes,
    (state: RootState) => state.notificationGroups.groups,
  ],
  filterNotificationsByAllowedTypes,
);

const selectPendingNotificationGroups = createSelector(
  [
    selectSettingsNotificationsQuickFilterShow,
    selectSettingsNotificationsQuickFilterActive,
    selectSettingsNotificationsExcludedTypes,
    (state: RootState) => state.notificationGroups.pendingGroups,
  ],
  filterNotificationsByAllowedTypes,
);

export const selectUnreadNotificationGroupsCount = createSelector(
  [
    (s: RootState) => s.notificationGroups.lastReadId,
    selectNotificationGroups,
    selectPendingNotificationGroups,
    (s: RootState) => s.notificationGroups.serverUnreadCount,
    (s: RootState) => s.notificationGroups.serverUnreadCountReadId,
  ],
  (
    notificationMarker,
    groups,
    pendingGroups,
    serverUnreadCount,
    serverUnreadCountReadId,
  ) => {
    function filterRelevantNotifications(
      group: NotificationGap | NotificationGroup,
    ) {
      if (isRedesignEnabled() && group.type === 'follow_request') {
        // Pending follow requests are counted separately in the redesign
        return false;
      }
      return (
        group.type !== 'gap' &&
        group.page_max_id &&
        compareId(group.page_max_id, notificationMarker) > 0
      );
    }

    const loadedUnreadCount =
      groups.filter(filterRelevantNotifications).length +
      pendingGroups.filter(filterRelevantNotifications).length;

    // Counting loaded groups saturates at one page, so the server count fills in
    // the rest. It only holds until something is read, which moves the marker.
    if (serverUnreadCountReadId !== notificationMarker)
      return loadedUnreadCount;

    return Math.max(loadedUnreadCount, serverUnreadCount);
  },
);

// Whether there is any unread notification according to the user-facing state
export const selectAnyPendingNotification = createSelector(
  [
    (s: RootState) => s.notificationGroups.readMarkerId,
    selectNotificationGroups,
  ],
  (notificationMarker, groups) => {
    return groups.some(
      (group) =>
        group.type !== 'gap' &&
        group.page_max_id &&
        compareId(group.page_max_id, notificationMarker) > 0,
    );
  },
);

export const selectPendingNotificationGroupsCount = createSelector(
  [selectPendingNotificationGroups],
  (pendingGroups) =>
    pendingGroups.filter((group) => group.type !== 'gap').length,
);
